import clsx from "clsx";
import { FC, useMemo } from "react";
import { useTranslation } from "react-i18next";
import useEvent from "react-use-event-hook";

import { PIC } from "@ryde/assets";
import { useDynamicFormsFetch } from "@ryde/redux";
import {
  IStationDto,
  NO_VALUE_DASH,
  onActivateKeyDown,
  useDrawers,
  useHasPermissions,
} from "@ryde/shared";

import { StationsMapStationInfoPopupStyled } from "./stations.map.styled";
import { useStationsAreaPolygons } from "./use-stations-area-polygons";

interface IStationsMapStationPopupProps {
  station: IStationDto;
}

export const StationsMapStationPopup: FC<IStationsMapStationPopupProps> = ({
  station,
}) => {
  const { t } = useTranslation();
  const { openEditFormDrawer } = useDrawers();
  const { fetchTableRows } = useDynamicFormsFetch("station");
  const { getShownPolygon, togglePolygon, loadingAreaId } =
    useStationsAreaPolygons();

  const { hasPermissions: canUpdateStation } = useHasPermissions({
    entity: "station",
    action: "update",
  });

  const areaName = station.areaName ?? "";
  const stationName = station.name ?? "";

  const shownPolygon = getShownPolygon(station.areaId);
  const isPolygonShown = Boolean(shownPolygon);
  const isPolygonLoading = loadingAreaId === station.areaId;

  const headerText = useMemo(() => {
    return stationName || areaName || `#${station.id}`;
  }, [areaName, stationName, station.id]);

  const attributes = useMemo(() => {
    const chips: {
      label: string;
      isOfficial?: boolean;
      isDestination?: boolean;
    }[] = [];

    if (station.metadata?.isOfficial) {
      chips.push({
        label: t("station.formLabel.isOfficial"),
        isOfficial: true,
      });
    }

    if (station.isDestination) {
      chips.push({
        label: t("station.formLabel.isDestination"),
        isDestination: true,
      });
    }

    return chips;
  }, [station, t]);

  const onTogglePolygon = useEvent((): void => {
    if (isPolygonLoading) {
      return;
    }

    togglePolygon(station);
  });

  const onEditClick = useEvent(async (): Promise<void> => {
    if (!canUpdateStation || !station.id) {
      return;
    }

    const res = await openEditFormDrawer({
      entity: "station",
      id: station.id,
    });

    if (res.success && res.data?.id) {
      fetchTableRows();
    }
  });

  return (
    <StationsMapStationInfoPopupStyled>
      <div className="station-info-header">
        <div className="station-name" title={headerText}>
          {headerText} ({station.id})
        </div>

        <div className="area-row">
          <span className="area-label">{t("station.mapAreaLabel")}:</span>
          {station.areaId ? (
            <span
              className={clsx("polygon-toggle", {
                shown: isPolygonShown,
                loading: isPolygonLoading,
              })}
              title={
                isPolygonShown
                  ? t("station.mapHideAreaPolygon")
                  : t("station.mapShowAreaPolygon")
              }
              onClick={onTogglePolygon}
              onKeyDown={onActivateKeyDown(onTogglePolygon)}
              role="button"
              tabIndex={0}
            >
              <span
                className="polygon-dot"
                style={
                  isPolygonShown
                    ? { backgroundColor: shownPolygon?.colorValue }
                    : undefined
                }
              />
            </span>
          ) : null}
          <span className="area-name" title={areaName}>
            {areaName || NO_VALUE_DASH}
          </span>
        </div>
      </div>

      <div className="attributes">
        {attributes.length > 0 ? (
          attributes.map(({ label, ...othersAttr }) => (
            <span
              key={label}
              className={clsx("attribute-chip", { ...othersAttr })}
            >
              {label}
            </span>
          ))
        ) : (
          <span className="empty">—</span>
        )}
      </div>

      <div className="footer">
        <div
          className={clsx("edit-button", { disabled: !canUpdateStation })}
          onClick={onEditClick}
          onKeyDown={onActivateKeyDown(onEditClick)}
          role="button"
          tabIndex={0}
        >
          <PIC.PencilSVG className="pencil-icon" />
          <span>{t("general.EDIT")}</span>
        </div>
      </div>
    </StationsMapStationInfoPopupStyled>
  );
};
